<template>
  <div
    class="collection-wrapper"
    :class="{ order: isOrderCollection, show: showCollection }"
  >
    <div
      ref="wrapperRef"
      class="slide-wrapper"
    >
      <div>
        <div
          v-for="(item, index) in feedsList"
          :key="item.id"
          ref="collectItem"
          class="feed-item-wrapper"
          :class="{ active: activeIndex === index }"
        >
          <div
            v-if="isOrderCollection"
            class="order-desc"
          >
            <div>{{ getCollectionIndex(item) }}</div>
            <div>{{ getCollectionEvent(item) }}</div>
          </div>
          <div
            class="feed-item"
            :style="{ backgroundImage: getImage(item) }"
            @click.stop="clickCollectionItem(item, index)"
          >
            <div>
              <i
                v-show="activeIndex === index"
                class="icon"
                :class="{ pausing: activePlayerPlaying }"
              />
            </div>
          </div>
        </div>
        <div class="feed-item-wrapper">
          <div
            class="feed-item check-last"
            @click="clickCheckLast"
          >
            <div>{{ lastItemTxt }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { toRefs, computed, ref, onMounted, watch, nextTick } from 'vue';
import { useStore } from '@/store/index';
import BScroll, { BScrollInstance } from '@better-scroll/core';
import { ModelMetaFeed } from '@/types/model';
import { report } from '@/libs/report';
import { fixHttps } from '@/utils/no-import-meta/url';
import { IS_SSR } from '@/utils/env';

const emits = defineEmits([
  'on-click-collectionItem',
  'on-click-checkLastCollection',
]);

// props
interface Props {
  feedsList: ModelMetaFeed[],
  activePlayerPlaying: boolean
}
const props = withDefaults(defineProps<Props>(), {
  activePlayerPlaying: false,
  feedsList: () => ([]),
});
const {
  feedsList,
} = toRefs(props);

// state
const { state } = useStore();
const showCollection = computed(() => state.showCollection);
const activeIndex = computed(() => state.activeIndex);
const collectionInfo = computed(() => state.collectionInfo);

// data
const scroll = ref<null | BScrollInstance>(null);
const collectItems = ref<HTMLElement[]>([]);
const wrapperRef = ref<null | HTMLElement>(null);

// computed
const lastItemTxt = computed(() => {
  const feedNum = collectionInfo.value?.feedNum || 0;
  if (feedNum > 10) {
    return `查看剩余${feedNum - feedsList.value.length}个视频`;
  }
  return '查看更多视频';
});
const isOrderCollection = computed(() => (collectionInfo.value?.collectionType !== 0));

// methods
const getImage = (feed: ModelMetaFeed) => `url(${fixHttps(feed.images[0]?.url)})`;
const getCollectionIndex = (feed: ModelMetaFeed) => feed.externInfo.mpEx.collectionIndex;
const getCollectionEvent = (feed: ModelMetaFeed) => feed.externInfo.mpEx.collectionEvent;
const scrollToElement = (tempIndex: number, speed = 300) =>  {
  if (!scroll.value) return;
  const index = Math.max(tempIndex, 1);
  scroll.value.scrollToElement(collectItems.value[index - 1], speed, false, false);
};
const clickCollectionItem = (item: ModelMetaFeed, index: number) => {
  scrollToElement(index);
  emits('on-click-collectionItem', item, index);
};
const clickCheckLast = () => {
  emits('on-click-checkLastCollection');
};

watch(activeIndex, (val) => {
  if (IS_SSR) return;
  nextTick(() => scrollToElement(val));
});

onMounted(() => {
  if (!wrapperRef.value) return;
  const wrapper = wrapperRef.value;
  collectItems.value =  wrapper.querySelectorAll('.feed-item-wrapper') as unknown as HTMLElement[]; // 缓存collectItems
  scroll.value = new BScroll(wrapper, { click: true });
  scrollToElement(activeIndex.value, 0);
  // 上报
  report('collection.back.exposure');
});

</script>

<style lang="scss" scoped>
.collection-wrapper {
  position: absolute;
  display: flex;
  transition: 0.3s ease;
  height: 100%;
  overflow: hidden;
  right: 0;
  z-index: 12;
  transform: translate3d(100%, 0, 0);
  &.show {
    transform: translate3d(0, 0, 0);
  }
  &.order {
    .slide-wrapper {
      @include responsiveStyleSingle(padding-right, 3.2, 'vw');
      .feed-item-wrapper {
        @include responsiveStyleSingle(margin-bottom, 3.7333, 'vw');
        &::after {
          position: absolute;
          top: 0;
          @include responsiveStyleSingle(bottom, -3.7333, 'vw');
          @include responsiveStyleSingle(right, -1.8, 'vw');
          content: '';
          @include responsiveStyleSingle(width, 0.4, 'vw');
          background: #3a3a3a;
        }
        &.active {
          .order-desc {
            color: #fff;
            &::after {
              background: #fff;
            }
          }
        }
        &:last-child {
          margin-bottom: 0;
        }
        &:first-child {
          &::after {
            @include responsiveStyleSingle(top,2.4,'vw');
          }
        }
        .feed-item {
          position: relative;
          @include responsiveStyleSingle(width, 19.4667,'vw');
          @include responsiveStyleSingle(height, 34.9333,'vw');
          margin-bottom: 0;
        }
      }
    }
  }
  .slide-wrapper {
    flex: 1;
    @include responsiveStyleSingle(margin-top, 17.0667,'vw');
    @include responsiveStyleSingle(margin-bottom, 2.6667,'vw');
    @include responsiveStyleSingle(padding-right, 2.1333,'vw');
    overflow: hidden;
    .feed-item-wrapper {
      &.active {
        .feed-item {
          padding: 0;
          border-color: #fff;
          border-style: solid;
          @include responsiveStyleSingle(border-width, 0.5333,'vw');
        }
      }
      .order-desc {
        position: relative;
        @include responsiveStyleSingle(line-height, 4.8, 'vw');
        color: rgba(255, 255, 255, 0.5);
        text-align: right;
        &::after {
          position: absolute;
          @include responsiveStyleSingle(top,2.4,'vw');
          @include responsiveStyleSingle(right, -1.6, 'vw');
          content: '';
          @include responsiveStyleSingle(height, 1.6, 'vw');
          @include responsiveStyleSingle(width, 1.6, 'vw');
          border-radius: 50%;
          background: #3a3a3a;
          transform: translate3d(50%, -50%, 0);
          z-index: 1;
        }
        div:first-child {
          @include responsiveStyleSingle(font-size, 3.2, 'vw');
        }
        div:last-child {
          @include responsiveStyleSingle(margin-top, 0.2667, 'vw');
          @include responsiveStyleSingle(margin-bottom, 0.5333, 'vw');
          @include responsiveStyleSingle(font-size, 3.4667, 'vw');
          font-weight: bold;
        }
      }
      .feed-item {
        position: relative;
        color: #fff;
        @include responsiveStyleSingle(width, 20.5333, 'vw');
        @include responsiveStyleSingle(height, 36.9333, 'vw');
        @include responsiveStyleSingle(padding, 0.2667, 'vw');
        @include responsiveStyleSingle(margin-bottom, 0.5333, 'vw');
        margin-left: auto;
        margin-right: auto;
        border-color: #3a3a3a;
        border-style: solid;
        @include responsiveStyleSingle(border-width, 0.2667,'vw');
        @include responsiveStyleSingle(border-radius, 1.0667,'vw');
        background-size: cover;
        background-position: center center;
        &.check-last {
          @include responsiveStyleSingle(font-size, 3.2, 'vw');
          div {
            @include responsiveStyleSingle(padding-left, 3.5333, 'vw');
            @include responsiveStyleSingle(padding-right, 3.5333, 'vw');
            background: rgba(255, 255, 255, 0.15);
          }
        }
        div {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          left: 0;
          top: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.1);
          .icon {
            @include responsiveStyleSingle(width, 5.3333, 'vw');
            @include responsiveStyleSingle(height, 5.3333, 'vw');
            background-image: url('https://sola.gtimg.cn/aoi/sola/20200707140935_2rGKINDqmM.png');
            background-size: 100% 100%;
            &.pausing {
              background-image: url('https://sola.gtimg.cn/aoi/sola/20200707140935_VoOMT5GDIp.png');
            }
          }
        }
      }
    }
  }
}
</style>
